// REGISTRUL SOLUȚIILOR, o dată pe proiect, pentru toate ecranele.
//
// Lanțul din antet, comutatorul, meniul vizualizatorului, pagina soluțiilor,
// centrul de activitate și furnizorul structurii citesc ACELAȘI registru
// (`GET /api/projects/:id/solutions`, contract §3). Înainte, fiecare ecran își
// cerea lista lui, iar structura avea și un registru al ei (Î23): două
// vocabulare, unite de mână, cu două ritmuri de sondaj.
//
// Sondajul e al contractului: la 3 s cât un rând are `rulare` sau cât
// `rulari_fara_solutie` nu e gol, altfel la 30 s — plus la revenirea în filă.
// Răspunsul poartă ETag, deci un sondaj fără nimic nou nu costă nimic și nu
// schimbă identitatea valorii (v. `nav/api.listSolutions`).
//
// Tot aici se nasc ANUNȚURILE de la final de fază (A9): între două sondaje,
// rândul care nu mai are rulare și a ajuns `gata` sau `nu_trece`.
//
// UN ID DIN ADRESĂ PE CARE REGISTRUL NU-L ARE nu e încă «necunoscut». După
// «Salvează ca Soluția N+1», după «Aplică pe soluție» sau după o pornire,
// ecranul merge pe rândul nou înainte ca sondajul (30 s, când nu rulează
// nimic) să-l fi adus: registrul ținut e cel vechi. Un astfel de id se caută
// o dată din nou, cu o citire proaspătă (fără ETag), iar abia dacă nici ea nu-l
// are ecranul spune «Soluția din adresă nu există» (`negasit`, `cautaDinNou`;
// v. `useFir`).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef,
         useState, type ReactNode } from "react";
import { listSolutions, uitaRegistrul, type RegistruSolutii } from "./api";
import { anunturi as anunturiNoi, type Anunt } from "./selectie";

export interface RegistruValoare {
  projectId: string;
  /** `null` până la primul răspuns */
  registru: RegistruSolutii | null;
  eroare: string | null;
  /** cere registrul acum; promisiunea se ține când a sosit */
  reincarca: () => Promise<RegistruSolutii | null>;
  /** cere registrul ÎNTREG, fără ETag: după o acțiune care a creat sau a șters
   *  rânduri, înainte de a naviga pe ele */
  reciteste: () => Promise<RegistruSolutii | null>;
  /** id-ul din adresă a fost căutat din nou, cu o citire proaspătă, și tot nu
   *  e în registru: abia atunci e «necunoscut» */
  negasit: (id: string) => boolean;
  /** caută din nou un id pe care registrul nu-l are; o dată pe id, cât timp
   *  lipsește */
  cautaDinNou: (id: string) => void;
  /** «Arată încercările interne» (pagina soluțiilor) */
  interne: boolean;
  setInterne: (v: boolean) => void;
  anunturi: Anunt[];
  inchideAnunt: (id: string) => void;
  /** un anunț care nu vine din sondaj: «Soluția 3 creată din Soluția 1» (A8) */
  anunta: (a: Anunt) => void;
}

const Ctx = createContext<RegistruValoare | null>(null);

export function useRegistru(): RegistruValoare {
  const v = useContext(Ctx);
  if (!v) throw new Error("useRegistru în afara lui <RegistruProvider>");
  return v;
}

/** Același, dar fără să ceară furnizorul: pentru componentele care se
 *  montează și în afara unui proiect (teste, ecrane de sine stătătoare). */
export const useRegistruOptional = () => useContext(Ctx);

/** Rulează ceva în proiect: ritmul sondajelor (3 s, altfel 30 s). */
export const areRulari = (r: RegistruSolutii | null) =>
  !!r && (r.rulari_fara_solutie.length > 0 || r.solutii.some((s) => s.rulare != null));

/** Cât stă un anunț pe ecran dacă nu-l închide nimeni. */
const ANUNT_MS = 12_000;

export function RegistruProvider({ projectId, children }:
                                 { projectId: string; children: ReactNode }) {
  const [registru, setRegistru] = useState<RegistruSolutii | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [interne, setInterne] = useState(false);
  const [anunturi, setAnunturi] = useState<Anunt[]>([]);
  const ultimul = useRef<RegistruSolutii | null>(null);
  /** id-urile căutate din nou și negăsite; `cautate` sunt cele pentru care
   *  citirea proaspătă a plecat deja (ca zece ecrane cu același id să nu
   *  ceară zece citiri) */
  const [negasite, setNegasite] = useState<ReadonlySet<string>>(() => new Set());
  const cautate = useRef(new Set<string>());

  useEffect(() => {
    setRegistru(null);
    ultimul.current = null;
    setAnunturi([]);
    setNegasite(new Set());
    cautate.current = new Set();
  }, [projectId]);

  const adaugaAnunturi = useCallback((vesti: Anunt[]) => {
    if (!vesti.length) return;
    setAnunturi((v) => [...v.filter((a) => !vesti.some((x) => x.id === a.id)), ...vesti]);
    for (const a of vesti) {
      // pașii fluxului de început stau până îi închide omul (sau până îi
      // înlocuiește următorul pas, pe același id)
      if (a.ramane) continue;
      setTimeout(() => setAnunturi((v) => v.filter((x) => x.id !== a.id)), ANUNT_MS);
    }
  }, []);

  const reincarca = useCallback(async () => {
    try {
      const nou = await listSolutions(projectId, { interne });
      setEroare(null);
      if (nou !== ultimul.current) {
        adaugaAnunturi(anunturiNoi(ultimul.current, nou, projectId));
        ultimul.current = nou;
        setRegistru(nou);
      }
      return nou;
    } catch (e) {
      setEroare((e as Error).message);
      return null;
    }
  }, [projectId, interne, adaugaAnunturi]);

  const reciteste = useCallback(async () => {
    uitaRegistrul();
    return reincarca();
  }, [reincarca]);

  const cautaDinNou = useCallback((id: string) => {
    if (cautate.current.has(id)) return;
    cautate.current.add(id);
    void reciteste().then((nou) => {
      if (nou?.solutii.some((s) => s.id === id)) {
        // găsit: dacă dispare mai târziu (o ștergere din altă filă), se caută
        // iar, nu se declară pe loc necunoscut
        cautate.current.delete(id);
        return;
      }
      // negăsit, sau citirea a picat: ecranul spune ce e, nu se învârte la
      // nesfârșit (eroarea registrului se arată separat, v. `EroareRegistru`)
      setNegasite((v) => new Set(v).add(id));
    });
  }, [reciteste]);
  const negasit = useCallback((id: string) => negasite.has(id), [negasite]);

  const viu = areRulari(registru);
  useEffect(() => {
    reincarca();
    const tic = setInterval(() => {
      if (!document.hidden) reincarca();
    }, viu ? 3_000 : 30_000);
    const acum = () => { if (!document.hidden) reincarca(); };
    // o soluție făcută DIN vizualizator (fațada aplicată) nu trece prin
    // niciun job: panoul o anunță pe `window`
    const dinAfara = () => { void reciteste(); };
    document.addEventListener("visibilitychange", acum);
    window.addEventListener("focus", acum);
    window.addEventListener("optimus:solutii", dinAfara);
    return () => {
      clearInterval(tic);
      document.removeEventListener("visibilitychange", acum);
      window.removeEventListener("focus", acum);
      window.removeEventListener("optimus:solutii", dinAfara);
    };
  }, [reincarca, reciteste, viu]);

  const inchideAnunt = useCallback(
    (id: string) => setAnunturi((v) => v.filter((a) => a.id !== id)), []);

  const anunta = useCallback((a: Anunt) => adaugaAnunturi([a]), [adaugaAnunturi]);

  const valoare = useMemo<RegistruValoare>(() => ({
    projectId, registru, eroare, reincarca, reciteste, negasit, cautaDinNou,
    interne, setInterne, anunturi, inchideAnunt, anunta,
  }), [projectId, registru, eroare, reincarca, reciteste, negasit, cautaDinNou,
       interne, anunturi, inchideAnunt, anunta]);
  return <Ctx.Provider value={valoare}>{children}</Ctx.Provider>;
}

/** Un registru dat, fără rețea — pentru teste și previzualizări. Un registru
 *  fix nu mai are ce aduce, deci un id care lipsește din el e negăsit pe loc.
 *  `eroare` imită o citire picată; `anunturi` primește ce s-a anunțat. */
export function RegistruFix({ projectId, registru, children, eroare = null, anunturi }:
                            { projectId: string; registru: RegistruSolutii | null;
                              children: ReactNode; eroare?: string | null;
                              anunturi?: Anunt[] }) {
  const valoare = useMemo<RegistruValoare>(() => ({
    projectId, registru, eroare,
    reincarca: async () => registru,
    reciteste: async () => registru,
    negasit: (id: string) => !registru?.solutii.some((s) => s.id === id),
    cautaDinNou: () => undefined,
    interne: false, setInterne: () => undefined,
    anunturi: anunturi ?? [], inchideAnunt: () => undefined,
    anunta: (a: Anunt) => { anunturi?.push(a); },
  }), [projectId, registru, eroare, anunturi]);
  return <Ctx.Provider value={valoare}>{children}</Ctx.Provider>;
}
